import { action, computed, type IReactionDisposer, makeObservable, observable, reaction } from "mobx";
import { newShortGuid } from "../util/types/guid";
import { type FileUpload, UploadStatus } from "./FileUpload";
import { groupBy } from "lodash";
import { parseDate } from "../util/date/parseDate";
import { formatOrDefault } from "../util/date/formatOrDefault";
import type { IUploadStore } from "./UploadStore";
import { StudyInUpload } from "./StudyInUpload";
import { mailRegex } from "../util/regex/regex";
import type { IPatientUploadCompleteCallbackStore } from "./PatientUploadCompleteCallbackStore";
import type { IUploadCodeAndSourceSelectionStore } from "./content/upload-code-and-source-selection/UploadCodeAndSourceSelectionStore";
import { throwError } from "../util/assertions/throwError";
import { delay } from "../util/functions/delay";
import { isNullOrWhiteSpace } from "../util/functions/isNullOrWhiteSpace";
import type { IDicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";
import { DicomTags } from "../app/viewer/network/contracts/dicom/DicomDataSet";

export interface IPatientToUpload extends Disposable {
    patientGuid: string;
    filesToUpload: FileUpload[];
    readyToUpload: boolean;
    patientName: string;
    patientDobFormatted: string;
    patientId: string;
    studies: StudyInUpload[];
    status: UploadStatus;
    canBeRemoved: boolean;
    totalUploadedInPercentage: number;
    totalUploadedInBytes: number;
    filesToUploadTotalSize: number;
    numberOfFilesDoneUploading: number;
    emailAddress: string;
    emailAddressIsValid: boolean;
    mobilePhone: string;
    privacyPolicyConsent: boolean;
    patientConsent: boolean;
    dicomFiles: FileUpload[];
    nonDicomFiles: FileUpload[];

    setEmailAddress(emailAddress: string): void;
    setMobilePhone(mobilePhone: string): void;
    addFiles(files: FileUpload[]): void;
    addNonDicomFileToStudy(file: FileUpload, study: StudyInUpload): void;
    removeFile(file: FileUpload): void;
    removeStudyInUpload(study: StudyInUpload): void;
    startQueueingUpload(): void;
    pauseUpload(): void;
    resumeUpload(): void;
    cancelUpload(): void;
    retryUpload(): void;
}

export class PatientToUpload implements IPatientToUpload {
    public patientGuid: string;

    @observable
    public filesToUpload: FileUpload[] = [];

    @observable
    public emailAddress = "";

    @observable
    public mobilePhone = "";

    @observable
    public patientConsent = false;

    @observable
    public privacyPolicyConsent = false;

    private _queueOrStartedChangedReaction: IReactionDisposer | null = null;
    private _statusChangedReaction: IReactionDisposer | null = null;
    private _filesChangedReaction: IReactionDisposer | null = null;
    private _filesUploadedReaction: IReactionDisposer | null = null;

    private static readonly RetryPolicy: number[] = [1, 2, 4, 8];

    constructor(
        filesToUpload: FileUpload[],
        private readonly uploadStore: IUploadStore,
        private readonly patientUploadCompleteCallbackStore: IPatientUploadCompleteCallbackStore,
        private readonly uploadCodeAndSourceSelectionStore: IUploadCodeAndSourceSelectionStore,
        private readonly dicomUploadTusFingerprintStore: IDicomUploadTusFingerprintStore,
    ) {
        makeObservable(this);

        this._queueOrStartedChangedReaction = reaction(
            () => [this.numberOfQueuedFiles, this.numberOfStartedFiles],
            () => this.checkQueuedUploads(),
        );

        this._statusChangedReaction = reaction(
            () => [this.status],
            async () => await this.statusChanged(),
        );

        this._filesChangedReaction = reaction(
            () => [this.filesToUpload, this.filesToUpload.length],
            async () => this.checkStudiesBasedOnFiles(),
        );

        this._filesUploadedReaction = reaction(
            () => this.numberOfFilesDoneUploading,
            numberOfUploadedFiles => this.clearLocalStorageForUploadedFiles(numberOfUploadedFiles),
        );

        this.filesToUpload = filesToUpload;
        this.patientGuid = newShortGuid();

        this.checkStudiesBasedOnFiles();
    }

    @computed
    public get dicomFiles(): FileUpload[] {
        return this.filesToUpload.filter(s => s.fileIsADicomFile);
    }

    @computed
    public get nonDicomFiles(): FileUpload[] {
        return this.filesToUpload.filter(s => !s.fileIsADicomFile);
    }

    @computed
    private get numberOfQueuedFiles(): number {
        return this.filesToUpload.filter(f => f.status === UploadStatus.Queued).length;
    }

    @computed
    private get numberOfStartedFiles(): number {
        return this.filesToUpload.filter(f => f.status === UploadStatus.Started).length;
    }

    @computed
    public get readyToUpload(): boolean {
        if (this.filesToUpload.length === 0) return false; //No uploadable files selected
        if (this.uploadStore.reachedMaxUploadSize) return false; //Reached max upload size
        if (this.status === UploadStatus.Started) return false; //Already started
        if (this.status === UploadStatus.Paused) return false; //Paused
        if (this.status === UploadStatus.Done) return false; //Already done
        if (!this.privacyPolicyConsent || !this.patientConsent) return false;

        return true;
    }

    @computed
    public get patientName(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.PatientName) ?? "";
    }

    @computed
    private get patientDob(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.PatientBirthDate) ?? "";
    }

    @computed
    public get patientDobFormatted(): string {
        const patientDob = this.patientDob;
        const date = parseDate(patientDob, "yyyyMMdd");
        return formatOrDefault(date, "dd/MM/yyyy", patientDob);
    }

    @computed
    public get patientId(): string {
        if (!this.dicomFiles || this.dicomFiles.length === 0) return "";
        return this.dicomFiles[0].getDicomTagValue(DicomTags.PatientID) ?? "";
    }

    private checkStudiesBasedOnFiles() {
        let newStudies: StudyInUpload[] = [];
        const groupedFiles = groupBy(this.filesToUpload, f => f.studyId);

        for (const key in groupedFiles) {
            const files = groupedFiles[key];
            const newStudy = new StudyInUpload(files);

            const oldStudy = this.studies.find(s => s.studyInstanceUid === key);
            if (oldStudy) {
                newStudy.setComment(oldStudy.comment);
            }

            newStudies.push(newStudy);
        }

        this.studies = newStudies;
    }

    @observable
    public studies: StudyInUpload[] = [];

    @computed
    public get status(): UploadStatus {
        //TOP to DOWN, at least one file with this status
        if (this.filesToUpload.some(u => u.status === UploadStatus.MaliciousContent)) {
            return UploadStatus.MaliciousContent;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.NotADicomFile)) {
            return UploadStatus.NotADicomFile;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.Failed)) {
            return UploadStatus.Failed;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.Cancelled)) {
            return UploadStatus.Cancelled;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.RateLimited)) {
            return UploadStatus.RateLimited;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.ExceededUploadSizeLimit)) {
            return UploadStatus.ExceededUploadSizeLimit;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.IpBlocked)) {
            return UploadStatus.IpBlocked;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.UploadCodeExpired)) {
            return UploadStatus.UploadCodeExpired;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.Paused)) {
            return UploadStatus.Paused;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.Started)) {
            return UploadStatus.Started;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.Queued)) {
            return UploadStatus.Queued;
        }
        if (this.filesToUpload.some(u => u.status === UploadStatus.ValidatingFiles)) {
            return UploadStatus.ValidatingFiles;
        }
        //at least one file is not done yet nor all of the above statuses -> we did not start yet
        if (this.filesToUpload.some(u => u.status !== UploadStatus.Done)) {
            return UploadStatus.NotStarted;
        }
        //none of the above -> we're completely done
        return UploadStatus.Done;
    }

    @computed
    public get canBeRemoved(): boolean {
        if (this.status === UploadStatus.NotStarted) return true; //Can only be removed when upload has not started yet
        return false;
    }

    @computed
    public get totalUploadedInPercentage() {
        return (this.totalUploadedInBytes / this.filesToUploadTotalSize) * 100;
    }

    @computed
    public get totalUploadedInBytes() {
        let totalBytesUploaded = 0;

        this.filesToUpload.forEach(f => {
            totalBytesUploaded += f.bytesUploaded;
        });

        return totalBytesUploaded;
    }

    @computed
    public get filesToUploadTotalSize(): number {
        let filesToUploadTotalSize = 0;

        this.filesToUpload.forEach(f => {
            filesToUploadTotalSize += f.size;
        });

        return filesToUploadTotalSize;
    }

    @computed
    public get numberOfFilesDoneUploading(): number {
        return this.filesToUpload.filter(f => f.arrivedAtTheBackend).length;
    }

    @computed
    public get emailAddressIsValid(): boolean {
        if (this.emailAddress.trim() === "") return true;

        return mailRegex.exec(this.emailAddress) !== null;
    }

    private checkQueuedUploads(): void {
        //Upload is paused or cancelled, don't check
        if (this.status === UploadStatus.Paused || this.status === UploadStatus.Cancelled) {
            return;
        }

        //Already at max uploading number of started files
        if (this.numberOfStartedFiles >= this.uploadStore.maxUploadFilesAtOnce) {
            return;
        }

        //Queue some more if below 50 queued files
        if (this.numberOfQueuedFiles < 50) {
            this.queueMoreUploads();
        }

        //Start number of queued files
        const queuedFilesToStart = this.filesToUpload.filter(f => f.status === UploadStatus.Queued)?.slice(0, this.uploadStore.maxUploadFilesAtOnce - this.numberOfStartedFiles);

        for (const queuedFileToStart of queuedFilesToStart) {
            queuedFileToStart.startUpload(this.patientGuid);
        }
    }

    private async statusChanged(): Promise<void> {
        if (this.status === UploadStatus.ValidatingFiles) {
            await this.startValidationProcess();
        }
    }

    private async startValidationProcess(): Promise<void> {
        await this.pollValidationStatusAsync();

        let retryPolicyIndex = 0;

        while (this.status === UploadStatus.ValidatingFiles) {
            await delay(1000 * PatientToUpload.RetryPolicy[retryPolicyIndex]);

            await this.pollValidationStatusAsync();

            if (retryPolicyIndex < PatientToUpload.RetryPolicy.length - 1) {
                retryPolicyIndex++;
            }
        }
    }

    private async pollValidationStatusAsync(): Promise<void> {
        const { uploadCodeAndSourceSelectionStore, dicomUploadTusFingerprintStore } = this;
        const uploadDict: Map<string, FileUpload> = new Map<string, FileUpload>();
        const fileUploads: FileUpload[] = [];
        for (const study of this.studies) {
            study.filesToUpload.forEach(fu => {
                uploadDict.set(fu.uploadFingerprint, fu);
                fileUploads.push(fu);
            });
        }

        const { patientUploadCompleteCallbackStore } = this;

        try {
            const studyInfoArray = this.studies.map(study => ({
                studyInstanceUid: study.studyInstanceUid,
                comment: study.comment ?? null,
                files: study.filesToUpload.map(fu => fu.uploadFingerprint),
            }));
            const completeUploadResult = await patientUploadCompleteCallbackStore.completeUpload(
                uploadCodeAndSourceSelectionStore.selectedSourceForUpload?.id ?? throwError("Source is required"),
                uploadCodeAndSourceSelectionStore.uploadCode,
                isNullOrWhiteSpace(this.emailAddress) ? null : this.emailAddress,
                isNullOrWhiteSpace(this.mobilePhone) ? null : this.mobilePhone,
                studyInfoArray,
            );

            const { maliciousFiles, notDicomFiles, successDicomFiles, successOtherFiles } = completeUploadResult;

            for (const maliciousFile of maliciousFiles) {
                uploadDict.get(maliciousFile)?.maliciousContentFound();
            }

            for (const notADicomFile of notDicomFiles) {
                uploadDict.get(notADicomFile)?.notADicomFile();
            }

            for (const successDicomFile of successDicomFiles) {
                uploadDict.get(successDicomFile)?.uploadComplete();
            }

            for (const successNonDicomFile of successOtherFiles) {
                uploadDict.get(successNonDicomFile)?.uploadComplete();
            }

            dicomUploadTusFingerprintStore.clearAllFingerprintsFromLocalStorage();
        } catch (error) {
            if (error.message.includes("ExceededUploadSizeLimit")) {
                for (const fileUpload of fileUploads) {
                    fileUpload.uploadExceededUploadSizeLimit();
                }
            } else {
                console.error("Something went wrong during the upload complete callback: ", error);
                for (const fileUpload of fileUploads) {
                    fileUpload.uploadFailed();
                }
            }
        }
    }

    private checkIfThereAreStillFilesToUpload() {
        if (this.filesToUpload.length === 0) {
            this.uploadStore.removePatientToUpload(this);
        } else {
            this.checkStudiesBasedOnFiles();
            this.studies.forEach(studyInUpload => {
                if (studyInUpload.dicomFiles.length === 0) {
                    this.removeStudyInUpload(studyInUpload);
                }
            });
        }
    }

    @action
    public setEmailAddress(emailAddress: string): void {
        this.emailAddress = emailAddress.trim();
        this.filesToUpload.forEach(f => {
            f.emailAddress = this.emailAddress;
        });
    }

    @action
    public setMobilePhone(mobilePhone: string): void {
        this.mobilePhone = mobilePhone;
        this.filesToUpload.forEach(f => {
            f.mobilePhone = this.mobilePhone;
        });
    }

    @action
    public addFiles(files: FileUpload[]): void {
        const newFiles = files.filter(file => !this.filesToUpload.some(f => f.file.name === file.file.name && f.file.size === file.file.size));

        if (newFiles.length > 0) {
            this.filesToUpload.push(...newFiles);
        }
    }

    @action
    public addNonDicomFileToStudy(file: FileUpload, study: StudyInUpload) {
        if (!this.filesToUpload.some(f => f.file.name === file.file.name && f.file.size === file.file.size)) {
            file.setPatientId(this.patientId);
            file.setStudyIdForNonDicomFile(study.studyInstanceUid);
            this.filesToUpload.push(file);
        }
    }

    @action
    public removeFile(file: FileUpload): void {
        this.filesToUpload = this.filesToUpload.filter(f => f.fileGuid !== file.fileGuid);
        this.checkIfThereAreStillFilesToUpload();
    }

    @action
    public removeStudyInUpload(study: StudyInUpload): void {
        const studyFilesGuids = new Set(study.filesToUpload.map(f => f.fileGuid));
        this.filesToUpload = this.filesToUpload.filter(f => !studyFilesGuids.has(f.fileGuid));
        this.checkIfThereAreStillFilesToUpload();
    }

    @action
    public setPrivacyPolicyConsent(value: boolean): void {
        this.privacyPolicyConsent = value;
    }

    @action
    public setPatientConsent(value: boolean): void {
        this.patientConsent = value;
    }

    public startQueueingUpload(): void {
        if (this.filesToUpload.length === 0) return;

        //START QUEUEING THE UPLOAD, NOT ALL AT ONCE
        this.queueMoreUploads();
    }

    private queueMoreUploads(): void {
        const nextFilesToQueue = this.filesToUpload.filter(f => f.status === UploadStatus.NotStarted).slice(0, 50 - this.numberOfQueuedFiles);
        for (const fileUpload of nextFilesToQueue) {
            fileUpload.queueUpload();
        }
    }

    public pauseUpload(): void {
        if (this.filesToUpload.length === 0) return;
        const filesToPause = this.filesToUpload.filter(f => f.status === UploadStatus.Started);
        for (const fileUpload of filesToPause) {
            fileUpload.pauseUpload();
        }
    }

    public cancelUpload(): void {
        if (this.filesToUpload.length === 0) return;
        const filesToCancel = this.filesToUpload.filter(f => f.status === UploadStatus.Started || f.status === UploadStatus.Queued);
        for (const fileUpload of filesToCancel) {
            fileUpload.cancelUpload();
        }
    }

    public resumeUpload(): void {
        //Coming from paused upload
        if (this.filesToUpload.length === 0) return;
        const filesToResume = this.filesToUpload.filter(f => f.status === UploadStatus.Paused);
        for (const fileUpload of filesToResume) {
            fileUpload.queueUpload();
        }
    }

    public retryUpload(): void {
        if (this.filesToUpload.length === 0) return;
        const filesToRetry = this.filesToUpload.filter(f => f.status === UploadStatus.Failed || f.status === UploadStatus.RateLimited);
        for (const fileToRetry of filesToRetry) {
            fileToRetry.retryUpload();
        }
        this.queueMoreUploads();
    }

    private _clearLocalStorageForUploadedFilesCallbackId: number | null = null;
    private clearLocalStorageForUploadedFiles(numberOfUploadedFiles: number): void {
        if (numberOfUploadedFiles % 500 !== 0) {
            return; // only remove fingerprints from local storage every 500 files
        }

        const uploadedFiles = this.filesToUpload.filter(file => file.arrivedAtTheBackend && file.keyInLocalStorage !== null);

        const { dicomUploadTusFingerprintStore } = this;

        this._clearLocalStorageForUploadedFilesCallbackId = requestIdleCallback(() => {
            if (this._clearLocalStorageForUploadedFilesCallbackId !== null) {
                cancelIdleCallback(this._clearLocalStorageForUploadedFilesCallbackId);
            }
            console.debug(`🧹 Removing local storage entries for uploaded files!`);
            dicomUploadTusFingerprintStore.clearFingerprintsFromLocalStorage(uploadedFiles);
            this._clearLocalStorageForUploadedFilesCallbackId = null;
        });
    }

    [Symbol.dispose](): void {
        if (this._queueOrStartedChangedReaction !== null) {
            this._queueOrStartedChangedReaction();
            this._queueOrStartedChangedReaction = null;
        }

        if (this._statusChangedReaction !== null) {
            this._statusChangedReaction();
            this._statusChangedReaction = null;
        }

        if (this._filesChangedReaction !== null) {
            this._filesChangedReaction();
            this._filesChangedReaction = null;
        }

        if (this._filesUploadedReaction !== null) {
            this._filesUploadedReaction();
            this._filesUploadedReaction = null;
        }
    }
}
